import moment from 'moment';
import { G2S } from '../g2s';
import * as api from '../api';
import * as flights from '../flights';
import * as utils from '../utils';
import { ProductPopupModel } from '../flights';

export class ReservationService {
    // Defines if checkbooking should be skipped on getFlightBookingDetails request.
    public static skipCheckBookingOnFlightBookingDetails = false;
    public static needToStopSbpPolling = false;

    bookProducts(request: api.BookProductsRequest): Promise<api.BookProductsResponse> {
        return new Promise<api.BookProductsResponse>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/BookProducts',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.BookProductsResponse>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    updateBookingSession(request: api.UpdateBookingSessionRequest) {
        return new Promise((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/UpdateBookingSession',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    updateBookingSessionProductPopup(request: api.UpdateBookingSessionProductPopupRequest) {
        return new Promise((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/UpdateBookingSessionProductPopup',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    updateBookingSessionNotEnoughMoneyPopup(request: api.UpdateBookingSessionNotEnoughMoneyPopupRequest) {
        return new Promise((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/UpdateBookingSessionNotEnoughMoneyPopup',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    cancelProduct(request: api.CancelProductRequest) {
        return new Promise((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/CancelProduct',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    cancelProductAfterPopup(request: any) {
        return new Promise((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/CancelProductAfterPopup',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    checkFlight(request: api.CheckFlightRequest): Promise<api.CheckFlightResponse> {
        return new Promise<api.CheckFlightResponse>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/CheckFlight',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.CheckFlightResponse>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    createOrRestartReservation(request: api.CreateReservationRequest): Promise<api.CreateReservationResult> {
        return new Promise<api.CreateReservationResult>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/CreateOrRestartReservation',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.CreateReservationResult>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    createPassengerCreateData(reservation: flights.ReservationModel): api.PassengerCreateData[] {
        const result: api.PassengerCreateData[] = [];
        let passengerWithSeatIndex = 0;

        reservation.passengers.forEach((p, i) => {
            const allowMidName = p.validationConstraints[p.documentType].constraints
                .find((constrain) => constrain.nameProperty === 'LastName').nameConstraint !== 'NotAllowed';
            const allowPeriodOfDocument = p.validationConstraints[p.documentType].constraints
                .find((constrain) => constrain.nameProperty === 'ValidityPeriodOfDocument').nameConstraint !== 'NotAllowed';
            
            const passengerCreateData: api.PassengerCreateData = {
                type: p.type,
                firstName: p.firstName ? p.firstName.toUpperCase() : '',
                middleName: allowMidName && p.middleName 
                    ? p.middleName.toUpperCase() 
                    : '',
                lastName: p.lastName ? p.lastName.toUpperCase() : '',
                birthday: p.birthday ? moment(p.birthday).format('YYYY-MM-DD') : '',
                gender: p.gender,
                card: p.card,
                cardAirlineId: p.cardAirlineId,
                chosenSeatId: Object.values(p.selectedFlightSeats).map((s: any) => s.id),
                citizenship: p.citizenship,
                documentNumber: p.documentNumber,
                documentValidTill: allowPeriodOfDocument && p.documentValidTill
                    ? moment(p.documentValidTill).format('YYYY-MM-DD')
                    : '',
                baggageOptions: p.baggageOptions
            };

            if (reservation.trains.length && p.type != api.PassengerType.infant) {
                passengerCreateData.trainPlaces = reservation.trains.map(train => train.selectedPlaces[passengerWithSeatIndex]);
                passengerWithSeatIndex++;
            }
            console.log(passengerCreateData);
            result.push(passengerCreateData);
        });

        return result;
    }

    createReservationRequest(
        searchId: string,
        reservation: flights.ReservationModel,
        flightBookingDetails: api.FlightBookingDetails,
        clientDevice: api.ClientDevice
    ): api.CreateReservationRequest {
        const selectedPaymentType = reservation.payment!.selectedPaymentType;
        const urlQuery = utils.Http.getUrlParameters();
        let partnerCookie = utils.Cookies.getPartnerCookie();
        const partnerCookieAsObject = utils.Cookies.partnerCookieStringToObject(partnerCookie);

        // Rewrite partner cookie value with URL params marker to fix browser tabs issue
        if (urlQuery['MarkerOwner'] && urlQuery['MarkerOwner'].length) {
            partnerCookieAsObject['mo'] = urlQuery['MarkerOwner'];

            if ((urlQuery['MarkerValue'] && urlQuery['MarkerValue'].length)
                || (urlQuery['Marker'] && urlQuery['Marker'].length)) {
                partnerCookieAsObject['mv'] = urlQuery['MarkerValue'] || urlQuery['Marker'];
            }

            partnerCookie = utils.Cookies.partnerCookieObjectToString(partnerCookieAsObject);
        }
        else if (urlQuery['mo'] && urlQuery['mo'].length) {
            partnerCookieAsObject['mo'] = urlQuery['mo'];

            if (urlQuery['mv'] && urlQuery['mv'].length) {
                partnerCookieAsObject['mv'] = urlQuery['mv'];
            }

            partnerCookie = utils.Cookies.partnerCookieObjectToString(partnerCookieAsObject);
        }

        const request: api.CreateReservationRequest =
        {
            adapterData: reservation.selectedFlight!.adapterData,
            timestamp: moment().toJSON(),
            searchId: searchId,
            flightId: reservation.selectedFlight!.uniqueId,
            bookingSessionId: flightBookingDetails.bookingSessionId,
            paymentTypeId: selectedPaymentType.paymentTypeId,
            passengersCreateData: {
                contactEmail: reservation.contacts.email,
                contactPhone: reservation.contacts.phone,
                passengers: this.createPassengerCreateData(reservation)
            },
            confirmationPageUrlTemplate: G2S.config.desktopUrl + '/go/order/payment/_reservationPublicId_/_billPublicId_',
            partnerCookie: partnerCookie,
            gaClientID: (<any>window).__gaClientID,
            products: [],
            bonusAmount: selectedPaymentType.paymentTypeId !== 'PayLater'
                ? reservation!.payment!.bonusAmount
                : 0,
            clientDevice: clientDevice,
            couponGuid: reservation.couponGuid!,
            baggageOptions: reservation.baggageOptions,
            brandChoiceType: reservation.brandChoiceType,
            baggageChoiceType: reservation.baggageChoiceType,
            markerReferrer: utils.Cookies.getMarkerReferrer(),
            queryString: window.location.search.toString()
        };

        // Override flightId for miltiFR brands.
        if (reservation.hasBrandGroups
            && reservation.brandGroups.length > 1) {
            const flightId: number[] = [];

            reservation.brandGroups.forEach((brandGroup) => {
                flightId.push(parseInt(brandGroup.selectedFlightId));
            });

            // Sorting is required
            flightId.sort();

            request.flightId = flightId.join('_');
        }

        reservation.calculatedCleanSelectedProducts.forEach(p => {
            const o: {
                id: number;
                options: any;
                choiceType: api.ProductChoiceType;
                discountRuleId?: number;
                secondProductId?: number;
            } = {
                id: p.product.id,
                options: p.selectedOptions,
                choiceType: p.choiceType
            };
            if (p.product.discountRuleId) {
                o.discountRuleId = p.product.discountRuleId;

                const popupSecondProduct = reservation.productPopups.find(
                    popup =>
                        popup.discountId === p.product.discountRuleId
                        && popup.secondProductId
                );
                if (popupSecondProduct) o.secondProductId = popupSecondProduct.secondProductId;
            }

            request.products.push(o);
        });
        
        if (selectedPaymentType.kind === 'CreditCardInplace'
        && !selectedPaymentType.isYandexPay) {
            request.creditCard = reservation.payment!.getCardWithEncryptedPacket() || undefined;
        } else if (selectedPaymentType.isYandexPay) {
            request.paymentMethod = reservation.payment!.paymentMethod;
        }

        if (request.flightId !== flightBookingDetails.selectedFlightUniqueId) {
            request.upgradedFromFlightId = flightBookingDetails.selectedFlightUniqueId;
        }

        return request;
    }

    CompleteStartedReservation(request: any): Promise<api.CreateReservationResult> {
        return new Promise<api.CreateReservationResult>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/CompleteStartedReservation',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.CreateReservationResult>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    RejectStartedReservation(request: any): Promise<api.StartedReservation> {
        return new Promise<api.StartedReservation>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/RejectStartedReservation',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    finishPreauth3ds(request: api.FinishPreauth3dsRequest): Promise<api.CreateReservationResult> {
        return new Promise<api.CreateReservationResult>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/FinishPreauth3ds',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.CreateReservationResult>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    getFlightBookingDetails(request: api.GetFlightBookingDetailsRequest): Promise<api.FlightBookingDetails> {
        return new Promise<api.FlightBookingDetails>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/GetFlightBookingDetails',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.FlightBookingDetails>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    getProductBookingDetails(request: api.ProductBookingDetailsRequest): Promise<api.ProductBookingDetails> {
        return new Promise<api.ProductBookingDetails>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/GetProductBookingDetails',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.ProductBookingDetails>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    getReservationPublicId(request: api.GetReservationPublicIdRequest): Promise<api.GetReservationPublicIdResponse> {
        return new Promise<api.GetReservationPublicIdResponse>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/GetReservationPublicId',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.GetReservationPublicIdResponse>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    getReservationStatus(reservationPublicId: string, getPaymentTypes: boolean, isRefreshRequest: boolean): Promise<api.GetReservationStatusResult> {
        return new Promise<api.GetReservationStatusResult>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/GetReservationStatus',
                    {
                        reservationPublicId: reservationPublicId,
                        getPaymentTypes: getPaymentTypes,
                        isRefreshRequest: isRefreshRequest
                    },
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.GetReservationStatusResult>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    getReservationAndBillStatus(reservationPublicId: string, billPublicId: string, isNeedRemoveProducts?: boolean): Promise<api.GetReservationStatusResult> {
        return new Promise<api.GetReservationStatusResult>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/GetReservationAndBillStatus',
                    {
                        reservationPublicId: reservationPublicId,
                        billPublicId: billPublicId,
                        isNeedRemoveProducts: isNeedRemoveProducts
                    },
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.GetReservationStatusResult>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    getReservationByBookingSession(bookingSessionId: number) {
        return new Promise<string>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/GetReservationByBookingSession',
                    {
                        bookingSessionId: bookingSessionId
                    },
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    yaCreatePayment(paymentData): Promise<any> {
        return new Promise<any>((resolve, reject) => {
            (<any>window).YaPay.createPayment(paymentData)
                .then(function (payment) { 
                    resolve(payment);
                })
                .catch(function (err) { 
                    reject(err);
                });
        });
    }

    pay(request: api.PayReservationRequest): Promise<api.CreateReservationResult> {
        return new Promise<api.CreateReservationResult>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/Pay',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.CreateReservationResult>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    processBeforeBooking(searchId: string, flightBookingDetails: api.FlightBookingDetails, reservationModel: flights.ReservationModel): Promise<boolean> {
        const reservationService = new api.ReservationService();

        const request: api.CreateReservationRequest = reservationService.createReservationRequest(
            searchId,
            reservationModel,
            flightBookingDetails!,
            G2S.config.clientDevice
        );

        return new Promise<boolean>((resolve, reject) => {
            // We should process before booking only once!
            if (reservationModel.processedBeforeBooking) {
                resolve(false);
            } else {
                reservationModel.processedBeforeBooking = true;

                reservationService.processBeforeFlightBooking(request).then(
                    (result: api.FlightBookingDetails) => {
                        // #4998
                        reservationModel.forwardKits = result.brandedProductKits ?? [];
                        if (!result.showPopups) {
                            reservationModel.productPopups = [];

                            console.log('Force no popups.');

                            resolve(false);
                        } else {
                            if (result.productPopups && result.productPopups.length > 0) {
                                // reservationModel.productPopups = [];

                                if (!reservationModel.productPopups) {
                                    reservationModel.productPopups = [];
                                }

                                result.productPopups.forEach(pp => {
                                    reservationModel.productPopups.push(new ProductPopupModel(pp));

                                    if (!utils.find(reservationModel.products,
                                        (p: flights.ProductModel) => {
                                            return p.product.id === pp.productId;
                                        })) {
                                    }
                                });
                                result.productPopups.forEach(pp => {
                                    if (pp.type === 'Discount') {
                                        reservationModel.productPopupsDiscount.push(new ProductPopupModel(pp));
                                    }
                                });

                                // Process products as well as there can be new one
                                result.products.forEach(p => {
                                    if (!reservationModel.products.find(existerProduct => existerProduct.product.id === p.id) || p.discountRuleId) {
                                        console.log('New product found: ' + p.id);
                                        reservationModel.rawProducts.push(new flights.ProductModel(p));
                                    }
                                });

                                resolve(true);
                            }
                            else {
                                resolve(false);
                            }
                        }
                    },
                    (error) => {
                        resolve(false);
                    }
                );
            }
        });
    }

    processBeforeFlightBooking(request: api.CreateReservationRequest): Promise<api.FlightBookingDetails> {
        return new Promise<api.FlightBookingDetails>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/ProcessBeforeFlightBooking',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.FlightBookingDetails>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    saveOpinion(request: api.SaveReservationOpinionRequest): Promise<boolean> {
        return new Promise<boolean>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/SaveOpinion',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    checkFlightConfirm(request: api.CheckFlightConfirmRequest) {
        return new Promise<boolean>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/CheckFlightConfirm',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then(() => {
                    resolve(true);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    async pollPaymentQrStatus(request: api.FinishPreauth3dsRequest): Promise<api.CreateReservationResult> {
        ReservationService.needToStopSbpPolling = false;

        const executePoll = async (resolve, reject) => {
            this.getPaymentQrStatus(request)
                .then((result: api.CreateReservationResult) => {
                    if (result.paymentInfo?.inPlace?.succeed) {
                        return resolve(result);
                    }

                    if (ReservationService.needToStopSbpPolling) {
                        return reject();
                    }

                    setTimeout(executePoll, 1000, resolve, reject);
                }, (error) => {
                    console.error('Payment error (SBP): '+ error.message);
                    setTimeout(executePoll, 1000, resolve, reject);
                });
        };

        return new Promise(executePoll);
    }
    
    async getPaymentQrStatus(request: api.FinishPreauth3dsRequest): Promise<api.CreateReservationResult> {
        return new Promise<api.CreateReservationResult>((resolve, reject) => {
            G2S
                .$http
                .post(
                    '/api/Reservation/GetPaymentQrStatus',
                    request,
                    {
                        withCredentials: true
                    }
                )
                .then((response) => {
                    resolve(<api.CreateReservationResult>response.data);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }
}
